import { EmptyBagMessageCard } from "@/components/Checkout/EmptyBagMessageCard";
import LandingPageWrapper from "@/components/pages/AppPages/Pages/components/LandingPageWrapper";

import { styled } from "@mui/material";

import { CheckoutContext } from "../checkoutUIMachine";
import { useCheckoutUI } from "../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../hooks/useCheckoutCartInfo";

import CollapsibleSection from "@/components/Checkout/components/CheckoutV2/CheckoutForm/components/CollapsibleSection";
import { PaymentMethodDisplay } from "@/components/Checkout/components/CheckoutV2/CheckoutForm/components/PaymentMethodDisplay";

import Loading from "@/components/Loading";
import { useAppSelector } from "@/redux/hooks";
import { useTrackActions } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  brandclub_colors,
  TABLET_SCREEN_SIZE
} from "@brandclub/common-ui";
import { useEffect, useState } from "react";
import CheckoutAccountInfo from "./components/accountInfo/CheckoutAccountInfo";
import { StatusIcon } from "./components/common/StatusIcons";
import OrderSummary, {
  FloatingOrderSummary
} from "./components/orderSummary/OrderSummary";
import { ExperimentalCheckoutPaymentEditForm } from "./components/payment/CheckoutPaymentEditForm";
import { RetailerConnect } from "./components/retailerConnect/RetailerConnect";
import { ShippingMethodForm } from "./components/shipping/ShippingMethodForm";
import CheckoutSignIn from "./components/signIn/CheckoutSignIn";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  height: "100%",
  gap: "10px",
  marginTop: "68px",
  flexDirection: "row",
  justifyContent: "space-between",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    flexDirection: "column",
    justifyContent: "flex-start",
    marginTop: "24px",
  },

  ".checkoutSubmitForm": {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      // paddingBottom: "48px",
    },
    width: "100%",
    maxWidth: 776,
    marginRight: 48,
    ".collapsible-section + .collapsible-section": {
      borderTop: `1px solid ${brandclub_colors.grayLight}`,
    },
    ".checkoutTitle": {
      fontSize: 28,
      fontWeight: "bold",
      paddingBottom: 5,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        fontSize: 22,
      },
    },
  },
  ".sideSummary": {
    display: "block",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: "none",
    },
  },

  ".title": {
    fontWeight: "bold",
    fontSize: 22,
    paddingBottom: 4,
  },

  ".mobileTotalAndButton": {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: "48px",
  },
}));

export const FormSectionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
}));

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  margin: "auto",
  maxHeight: 250,
  maxWidth: "90%",
});

const MobileCheckoutForm = () => {
  const userProfile = useAppSelector((state) => state.userProfile);
  const [state, send] = useCheckoutUI();
  const { items } = useCheckoutCartInfo();
  const [trackAction] = useTrackActions();
  // @ts-ignore
  const { payment, shipping, checkoutIntent, retailerLogin, checkoutIntentLoading, checkoutResponse } =
    state.context as unknown as CheckoutContext;
  const [showEnabledSubmitButton, setShowEnabledSubmitButton] = useState(false);


  const orderId = checkoutResponse?.userRetailerCheckoutOrder?.orderId;
  const isPostSubmission = !!orderId;
  useEffect(() => {
    const eventParams = {
      checkoutIntentId: checkoutIntent?.checkoutIntentId,
      checkoutItems: items,
      checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
      ...(orderId && { orderId }),
    };
    trackAction(AnalyticsTrackingEvent.CHECKOUT_PAGE_VIEW, eventParams);
    trackAction(AnalyticsTrackingEvent.PAGE_VIEW, eventParams);
  }, [trackAction, checkoutIntent, isPostSubmission, orderId, items]);

  useEffect(() => {
    if (state.value === "ready_to_checkout" || state.value === "checking_out") {
      setShowEnabledSubmitButton(true);
    }
  }, [state.value]);

  const shippingInput = checkoutIntent?.shippingAddress;
  const loading = false;

  const toggleSection = (
    step: keyof Pick<CheckoutContext, "shipping" | "payment" | "retailerLogin">
  ) => {
    send({ type: "TOGGLE", data: { section: step } });
  };



  if (!userProfile?.userId) {
    return (
      <LandingPageWrapper includeFooter={false}>
        <Container>
          <div className="checkoutSubmitForm">
            <CheckoutSignIn />
          </div>
          <div
            className="sideSummary"
            style={{
              display: "block",
            }}
          >
            <OrderSummary showSubmitButton={showEnabledSubmitButton} />
          </div>
        </Container>
      </LandingPageWrapper>
    );
  }

  if (checkoutIntentLoading) {
    return <Loading dot />;
  }

  if (!items.length) {
    return <StyledEmptyBagMessageCard />;
  }

  return (
    <LandingPageWrapper includeFooter={false}>
      <Container>
        <div className="checkoutSubmitForm">
          <div className="checkoutTitle">Checkout</div>
          <CollapsibleSection
            hideControl={true}
            isOpen={true}
            disabled={false}
            loading={false}
            title={"Your account"}
          >
            <CheckoutAccountInfo />
          </CollapsibleSection>

          {state.value !== "initial" ? (
            <>
              {payment.visible && (
                <CollapsibleSection
                  isOpen={payment.open}
                  loading={loading}
                  disabled={!payment.canOpen}
                  onToggle={() => {
                    toggleSection("payment");
                  }}
                  titleIcon={
                    <StatusIcon
                      success={!payment?.forceComplete}
                      successColor="rgb(33, 169, 111)"
                      errorColor="rgb(157, 56, 65)"
                    />
                  }
                  title={"Payment"}
                  collapsedContent={
                    checkoutIntent?.stripePaymentMethod ? (
                      <div style={{ paddingTop: "16px" }}>
                        <PaymentMethodDisplay
                          paymentMethod={checkoutIntent.stripePaymentMethod}
                        />
                      </div>
                    ) : (
                      "Enter your preferred payment method"
                    )
                  }
                  style={{
                    opacity:
                      checkoutIntent?.stripePaymentMethod ||
                        payment.open ||
                        payment.canOpen
                        ? 1
                        : 0.5,
                  }}
                >
                  <ExperimentalCheckoutPaymentEditForm
                    formVisible={payment.open}
                  />
                </CollapsibleSection>
              )}

              {!payment.forceComplete && shipping.visible && (
                <CollapsibleSection
                  isOpen={shipping.open}
                  disabled={!shipping.canOpen}
                  loading={loading}
                  title={"Ship to"}
                  titleIcon={
                    <StatusIcon
                      success={!shipping.forceComplete}
                      successColor="rgb(33, 169, 111)"
                      errorColor="rgb(157, 56, 65)"
                    />
                  }
                  onToggle={() => {
                    toggleSection("shipping");
                  }}
                  style={{
                    opacity: shipping.open || shipping.canOpen ? 1 : 0.5,
                  }}
                  collapsedContent={
                    shipping.forceComplete ? (
                      "Enter your preferred shipping address"
                    ) : (
                      <div style={{ paddingTop: "16px" }}>
                        <div>
                          {shippingInput?.firstName} {shippingInput?.lastName}
                        </div>
                        <div>
                          {shippingInput?.address1}, {shippingInput?.address2}
                          {shippingInput?.address2 ? "," : ""}{" "}
                          {shippingInput?.city}, {shippingInput?.state}{" "}
                          {shippingInput?.zipCode}
                        </div>
                      </div>
                    )
                  }
                >
                  <ShippingMethodForm />
                </CollapsibleSection>
              )}

              {/* Retailer Login */}
              {!payment.forceComplete && retailerLogin.visible && (
                <CollapsibleSection
                  hideControl={true}
                  isOpen={true}
                  disabled={false}
                  loading={false}
                  title={"Retailer account"}
                  titleIcon={
                    <StatusIcon
                      success={!!checkoutIntent?.retailerConnection}
                      successColor="rgb(33, 169, 111)"
                      errorColor="rgb(157, 56, 65)"
                    />
                  }
                >
                  <RetailerConnect />
                </CollapsibleSection>
              )}
              <CollapsibleSection
                hideControl={true}
                isOpen={true}
                disabled={false}
                loading={false}
                title={"Order summary"}
                sx={{
                  display: "none",
                  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                    display: "block",
                  },
                }}
              >
                <OrderSummary
                  showSubmitButton={showEnabledSubmitButton}
                  showOrderSummaryTitle={false}
                />
              </CollapsibleSection>
            </>
          ) : null}
        </div>

        <div className="sideSummary">
          <OrderSummary showSubmitButton={showEnabledSubmitButton} />
        </div>

        {showEnabledSubmitButton && <FloatingOrderSummary />}
      </Container>
    </LandingPageWrapper>
  );
};

export default MobileCheckoutForm;
